import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { RegionProvider } from './context/RegionContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { ArticlePage } from './pages/ArticlePage';
import { CategoryPage } from './pages/CategoryPage';
import { SearchPage } from './pages/SearchPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminArticleEditor } from './pages/AdminArticleEditor';
import { AdminPSEO } from './pages/AdminPSEO';
import { AdminUsers } from './pages/AdminUsers';
import { AdminAuthors } from './pages/AdminAuthors';
import { AdminCategories } from './pages/AdminCategories';
import { AdminBrandCategories } from './pages/AdminBrandCategories';
import { BrandsPage } from './pages/BrandsPage';
import { BrandDealsPage } from './pages/BrandDealsPage';
import { SaleEventsPage } from './pages/SaleEventsPage';
import { SaleEventDetailPage } from './pages/SaleEventDetailPage';
import { Toaster } from 'sonner';
import '@/App.css';

const AboutPage = () => (
  <div className="min-h-screen" data-testid="about-page">
    <div className="bg-brand-green text-white py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-green-300 uppercase tracking-widest text-sm font-semibold mb-4">Who We Are</p>
        <h1 className="text-5xl md:text-6xl font-playfair font-bold mb-6">About iShopGenie</h1>
        <p className="text-green-100 text-xl leading-relaxed max-w-2xl">
          Your trusted companion for smarter shopping — helping millions of buyers discover the best deals, honest reviews, and expert buying guides every day.
        </p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

      <div className="mb-16">
        <h2 className="text-3xl font-playfair font-bold text-gray-900 mb-6">Our Story</h2>
        <p className="text-gray-700 leading-relaxed text-lg mb-4">
          iShopGenie was born from a simple frustration: too many people were overpaying for products they could have got for less — simply because they didn't know where to look.
        </p>
        <p className="text-gray-700 leading-relaxed text-lg mb-4">
          We set out to build the most reliable, well-researched shopping resource on the internet. One that cuts through the noise, verifies the deals, and gives shoppers the knowledge they need to buy with confidence.
        </p>
        <p className="text-gray-700 leading-relaxed text-lg">
          Today, iShopGenie covers everything from electronics and fashion to beauty and travel — with expert buying guides, curated brand deals, and up-to-date coupon codes updated around the clock.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
          <div className="text-4xl mb-4">🎯</div>
          <h3 className="text-xl font-playfair font-bold text-gray-900 mb-3">Our Mission</h3>
          <p className="text-gray-600 leading-relaxed">To empower every shopper with the information, deals, and guidance they need to make smarter purchases — and never overpay again.</p>
        </div>
        <div className="bg-red-50 rounded-2xl p-8 border border-red-100">
          <div className="text-4xl mb-4">👁️</div>
          <h3 className="text-xl font-playfair font-bold text-gray-900 mb-3">Our Vision</h3>
          <p className="text-gray-600 leading-relaxed">To become the most trusted shopping guide in Asia — a place where quality content, honest reviews, and real savings come together in one place.</p>
        </div>
      </div>

      <div className="mb-16">
        <h2 className="text-3xl font-playfair font-bold text-gray-900 mb-8">What We Stand For</h2>
        <div className="space-y-4">
          {[
            { icon: '✅', title: 'Verified Deals Only', desc: 'Every coupon code and offer on iShopGenie is checked before it goes live. No expired deals, no false discounts.' },
            { icon: '📖', title: 'Expert Buying Guides', desc: 'Our guides are researched thoroughly — covering products across every budget and need, with clear recommendations.' },
            { icon: '🔒', title: 'No Sponsored Bias', desc: 'Our editorial recommendations are independent. We never let brand partnerships influence our honest opinions.' },
            { icon: '⚡', title: 'Always Up to Date', desc: 'Our team monitors deals, launches, and price changes daily so you always have the freshest information.' },
          ].map((item, i) => (
            <div key={i} className="flex gap-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-brand-green rounded-2xl p-10 text-white text-center">
        <h2 className="text-3xl font-playfair font-bold mb-4">Want to work with us?</h2>
        <p className="text-green-100 mb-6 text-lg">Whether you're a brand, an affiliate, or a fellow content creator — we'd love to connect.</p>
        <a href="mailto:partnerships@ishopgenie.co" className="inline-block bg-white text-brand-green font-bold px-8 py-3 rounded-xl hover:bg-green-50 transition-colors">
          partnerships@ishopgenie.co
        </a>
      </div>

    </div>
  </div>
);

const ContactPage = () => (
  <div className="min-h-screen bg-gray-50" data-testid="contact-page">
    <div className="bg-brand-green text-white py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-green-300 uppercase tracking-widest text-sm font-semibold mb-4">Get In Touch</p>
        <h1 className="text-5xl md:text-6xl font-playfair font-bold mb-6">Contact Us</h1>
        <p className="text-green-100 text-xl leading-relaxed max-w-2xl">
          Have a question, a story tip, or want to explore a partnership? We're always happy to hear from you.
        </p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {[
          { icon: '✉️', title: 'General Enquiries', email: 'partnerships@ishopgenie.co', desc: 'For questions, feedback, or anything else.' },
          { icon: '🤝', title: 'Brand Partnerships', email: 'partnerships@ishopgenie.co', desc: 'Collaborate with us on deals, reviews, and guides.' },
          { icon: '✍️', title: 'Editorial & Content', email: 'partnerships@ishopgenie.co', desc: 'Press, media, or content contribution enquiries.' },
        ].map((item, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
            <div className="text-4xl mb-4">{item.icon}</div>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
            <p className="text-gray-500 text-sm mb-4">{item.desc}</p>
            <a href={`mailto:${item.email}`} className="text-brand-red font-semibold text-sm hover:underline break-all">
              {item.email}
            </a>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-8">
        <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-2">Frequently Asked Questions</h2>
        <p className="text-gray-500 mb-6 text-sm">Before reaching out, you might find your answer here.</p>
        <div className="space-y-5">
          {[
            { q: 'How do I submit a deal or coupon code?', a: 'Email us at partnerships@ishopgenie.co with the deal details and we\'ll review it for publication.' },
            { q: 'How quickly do you respond to emails?', a: 'We aim to respond to all enquiries within 1–2 business days.' },
            { q: 'Can I advertise on iShopGenie?', a: 'Yes! We offer a range of partnership options for brands. Reach out to partnerships@ishopgenie.co to explore opportunities.' },
            { q: 'I found an expired deal — how do I report it?', a: 'Please email us with the deal URL and we\'ll remove or update it promptly. Thank you for helping us keep the site accurate!' },
          ].map((item, i) => (
            <div key={i} className="border-b border-gray-100 pb-5 last:border-0 last:pb-0">
              <h4 className="font-bold text-gray-900 mb-2">Q: {item.q}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-brand-green rounded-2xl p-8 text-white text-center">
        <h2 className="text-2xl font-playfair font-bold mb-2">We typically respond within 24 hours</h2>
        <p className="text-green-100 mb-4">Drop us a line and we'll get back to you as soon as possible.</p>
        <a href="mailto:partnerships@ishopgenie.co" className="inline-block bg-white text-brand-green font-bold px-8 py-3 rounded-xl hover:bg-green-50 transition-colors">
          partnerships@ishopgenie.co
        </a>
      </div>
    </div>
  </div>
);

const TermsPage = () => (
  <div className="min-h-screen" data-testid="terms-page">
    <div className="bg-gray-900 text-white py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-gray-400 uppercase tracking-widest text-sm font-semibold mb-4">Legal</p>
        <h1 className="text-5xl md:text-6xl font-playfair font-bold mb-6">Terms of Use</h1>
        <p className="text-gray-300 text-lg">Last updated: March 2026</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="prose prose-lg max-w-none space-y-10">

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
          <p className="text-gray-700 leading-relaxed">By accessing or using iShopGenie ("the Website"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our Website. We reserve the right to update these terms at any time, and your continued use of the Website constitutes your acceptance of any changes.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">2. About iShopGenie</h2>
          <p className="text-gray-700 leading-relaxed">iShopGenie is a shopping guide and deals platform that provides information about products, deals, coupons, and buying guides. We are an independent editorial platform and may earn commissions through affiliate links at no additional cost to you. Our editorial content and recommendations are not influenced by these partnerships.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">3. Accuracy of Information</h2>
          <p className="text-gray-700 leading-relaxed">We make every effort to ensure that deals, prices, and product information published on iShopGenie are accurate and up to date. However, prices and availability can change rapidly, and we cannot guarantee that all information is current at the time of your visit. We encourage you to verify deals directly with the retailer before making a purchase.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">4. Affiliate Disclosure</h2>
          <p className="text-gray-700 leading-relaxed">iShopGenie participates in affiliate marketing programmes. This means we may earn a small commission when you click on certain links and make a purchase. This comes at no extra cost to you and helps us maintain and grow our platform. We only recommend products and services we believe provide genuine value to our readers.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
          <p className="text-gray-700 leading-relaxed">All content on iShopGenie — including articles, guides, images, logos, and design — is the property of iShopGenie and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from our content without prior written permission.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">6. User Conduct</h2>
          <p className="text-gray-700 leading-relaxed">You agree to use iShopGenie only for lawful purposes. You must not attempt to gain unauthorised access to any part of the Website, disrupt its operation, or use it in any way that violates applicable laws or regulations.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">7. Third-Party Links</h2>
          <p className="text-gray-700 leading-relaxed">iShopGenie contains links to third-party websites and retailers. We are not responsible for the content, privacy practices, or availability of these external sites. Visiting third-party links is at your own risk.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
          <p className="text-gray-700 leading-relaxed">To the fullest extent permitted by law, iShopGenie shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of, or inability to use, this Website or any content therein.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">9. Contact</h2>
          <p className="text-gray-700 leading-relaxed">If you have any questions about these Terms of Use, please contact us at <a href="mailto:partnerships@ishopgenie.co" className="text-brand-red hover:underline">partnerships@ishopgenie.co</a>.</p>
        </div>

      </div>
    </div>
  </div>
);

const PrivacyPage = () => (
  <div className="min-h-screen" data-testid="privacy-page">
    <div className="bg-gray-900 text-white py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-gray-400 uppercase tracking-widest text-sm font-semibold mb-4">Legal</p>
        <h1 className="text-5xl md:text-6xl font-playfair font-bold mb-6">Privacy Policy</h1>
        <p className="text-gray-300 text-lg">Last updated: March 2026</p>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="prose prose-lg max-w-none space-y-10">

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">1. Introduction</h2>
          <p className="text-gray-700 leading-relaxed">iShopGenie ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our Website at ishopgenie.co. By using our Website, you consent to the practices described in this policy.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">2. Information We Collect</h2>
          <p className="text-gray-700 leading-relaxed mb-3">We may collect the following types of information:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Email address</strong> — when you subscribe to our newsletter or deal alerts.</li>
            <li><strong>Usage data</strong> — including pages visited, time spent on pages, and links clicked, collected via analytics tools.</li>
            <li><strong>Device and browser information</strong> — automatically collected when you visit our Website.</li>
            <li><strong>Cookies</strong> — small files stored on your device to improve your experience and track usage patterns.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-3">We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Send you deal alerts and newsletters (only if you have subscribed).</li>
            <li>Analyse and improve the performance and content of our Website.</li>
            <li>Understand how visitors use iShopGenie to make it more useful.</li>
            <li>Comply with legal obligations.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">4. Cookies</h2>
          <p className="text-gray-700 leading-relaxed">We use cookies and similar tracking technologies to enhance your experience on iShopGenie. You can control cookie settings through your browser. Please note that disabling cookies may affect the functionality of certain parts of the Website.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
          <p className="text-gray-700 leading-relaxed">We use third-party services including analytics providers and affiliate networks that may collect data about your use of our Website. These services have their own privacy policies governing the use of that information. We do not sell your personal data to third parties.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">6. Data Retention</h2>
          <p className="text-gray-700 leading-relaxed">We retain your email address for as long as you remain subscribed to our communications. You may unsubscribe at any time by clicking the unsubscribe link in any email we send you. Upon unsubscribing, your email will be removed from our mailing list promptly.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">7. Your Rights</h2>
          <p className="text-gray-700 leading-relaxed">You have the right to access, correct, or request deletion of any personal data we hold about you. To exercise these rights, please contact us at <a href="mailto:partnerships@ishopgenie.co" className="text-brand-red hover:underline">partnerships@ishopgenie.co</a>.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">8. Security</h2>
          <p className="text-gray-700 leading-relaxed">We take reasonable technical and organisational measures to protect your personal data against unauthorised access, loss, or misuse. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.</p>
        </div>

        <div>
          <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4">10. Contact Us</h2>
          <p className="text-gray-700 leading-relaxed">If you have any questions or concerns about this Privacy Policy, please contact us at <a href="mailto:partnerships@ishopgenie.co" className="text-brand-red hover:underline">partnerships@ishopgenie.co</a>.</p>
        </div>

      </div>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RegionProvider>
          <div className="App min-h-screen flex flex-col">
            <Toaster position="top-right" richColors />
            <Header />
            <main className="flex-1">
              <Routes>
                {/* Public Frontend Routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/article/:slug" element={<ArticlePage />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                
                {/* pSEO Routes - Programmatic SEO Pages */}
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/brands/:slug" element={<BrandDealsPage />} />
                <Route path="/brands/:slug/deals" element={<BrandDealsPage />} />
                <Route path="/sale-events" element={<SaleEventsPage />} />
                <Route path="/sale-events/:slug" element={<SaleEventDetailPage />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/articles/new" element={<AdminArticleEditor />} />
                <Route path="/admin/articles/edit/:id" element={<AdminArticleEditor />} />
                <Route path="/admin/pseo" element={<AdminPSEO />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/authors" element={<AdminAuthors />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/brand-categories" element={<AdminBrandCategories />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </RegionProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;